<script lang="ts" setup generic="T extends string | Component">
import {
  computed,
  useCssModule,
  useSlots,
  h,
  mergeProps,
  useAttrs,
  type Component,
  type VNodeChild,
} from 'vue';
import type { PolymorphicProps } from '../../shared/types';
const props = defineProps<
  PolymorphicProps<T> & {
    disabled?: boolean;
    active?: boolean;
    size?: 'xs' | 's' | 'm' | 'l';
    variant?: 'primary' | 'secondary' | 'tertiary' | 'inverted';
  }
>();
defineSlots<{
  default?: () => VNodeChild;
}>();
const style = useCssModule();
const rootProps = computed(() => {
  return {
    class: [
      style.root,
      {
        [style.sizeXs]: props.size === 'xs',
        [style.sizeS]: props.size === 's',
        [style.sizeM]: props.size === 'm',
        [style.sizeL]: props.size === 'l',
        [style.variantSecondary]: props.variant === 'secondary',
        [style.variantTertiary]: props.variant === 'tertiary',
        [style.variantInverted]: props.variant === 'inverted',
        [style.disabled]: props.disabled,
        [style.active]: props.active,
      },
    ],
    'data-ui-component': 'UiButtonIcon',
  };
});
const slots = useSlots();
const attrs = useAttrs();
defineRender(() => {
  const content = slots.default?.();
  if (props.as) {
    return h(props.as, mergeProps(attrs, rootProps.value, props.asProps ?? {}), () => [content]);
  }
  return h('button', { ...rootProps.value, type: 'button', disabled: props.disabled }, content);
});
</script>

<style lang="scss" module>
@use '../../styles/shared';

.root {
  @include shared.reset-button();
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  border-radius: 6px;
  height: 28px;
  width: 28px;
  flex: none;
  background: #226b6c;
  color: #f5f5f5;
  border: 1px solid #226b6c;
  padding: 4px;
  font-size: 0;

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }

  &:hover:not(.disabled) {
    background:
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%), #226b6c;
  }

  &:active:not(.disabled),
  &.active:not(.disabled) {
    background:
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%),
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%), #226b6c;
  }

  &:disabled,
  &.disabled {
    color: #d2d3d9;
    background: #ebecf2;
    border-color: #ebecf2;
    cursor: not-allowed;
  }

  > img,
  > svg {
    width: 100%;
    height: 100%;
  }
}

.variantSecondary,
.variantTertiary,
.variantInverted {
  color: #010000;

  &:hover:not(.disabled) {
    background: rgba(35, 57, 93, 0.05);
  }

  &:active:not(.disabled),
  &.active:not(.disabled) {
    background: rgba(35, 57, 93, 0.1);
    color: #226b6c;
  }
}

.variantSecondary {
  border-color: #d5d6d9;
  background: transparent;
}

.variantTertiary {
  border-color: transparent;
  background: transparent;

  &.disabled {
    background: transparent;
    border-color: transparent;
  }
}

.variantInverted {
  background: #fff;
  border-color: #fff;
}

.sizeXs {
  height: 18px;
  width: 18px;
  padding: 2px - 1px;
}

.sizeS {
  height: 22px;
  width: 22px;
  padding: 4px - 1px;
}

.sizeL {
  height: 36px;
  width: 36px;
  padding: 6px;
}
</style>
